/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to error reporting service in production
    this.setState({
      error,
      errorInfo
    });

    // Only log in development
    if (process.env.NODE_ENV !== 'production') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // In production, you would send this to an error reporting service
    // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            minHeight: '100vh',
            background: 'linear-gradient(120deg, #f7f8ff 60%, #f8e0f5 100%)',
            padding: '2rem'
          }}
        >
          <div className="text-center" style={{ maxWidth: '600px' }}>
            <div
              style={{
                fontSize: '6rem',
                fontWeight: 'bold',
                background: 'linear-gradient(90deg, #6B46C1, #D53F8C)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                lineHeight: 1,
                marginBottom: '1rem'
              }}
            >
              ⚠️
            </div>
            <h1
              className="mb-3 fw-bold"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '2.5rem',
                color: '#23284b'
              }}
            >
              Something Went Wrong
            </h1>
            <p
              className="mb-4 fs-5"
              style={{ color: '#344476' }}
            >
              We're sorry, but something unexpected happened. Our team has been notified and is working on a fix.
            </p>
            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
              <button
                onClick={this.handleReset}
                className="btn btn-lg rounded-pill px-5 py-3 fw-bold text-white"
                style={{
                  background: 'linear-gradient(90deg, #6B46C1, #D53F8C)',
                  border: 'none',
                  boxShadow: '0 4px 16px rgba(107, 70, 193, 0.3)'
                }}
              >
                Go to Homepage
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn btn-lg rounded-pill px-5 py-3 fw-bold"
                style={{
                  background: '#fff',
                  color: '#6B46C1',
                  border: '2px solid #6B46C1',
                  boxShadow: '0 4px 16px rgba(107, 70, 193, 0.2)'
                }}
              >
                Reload Page
              </button>
            </div>
            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <details className="mt-4 text-start" style={{ marginTop: '2rem' }}>
                <summary style={{ cursor: 'pointer', color: '#6B46C1' }}>
                  Error Details (Development Only)
                </summary>
                <pre
                  style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    overflow: 'auto',
                    fontSize: '0.875rem',
                    marginTop: '1rem'
                  }}
                >
                  {this.state.error && this.state.error.toString()}
                  <br />
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

